import * as ImageDataEdit from './ImageDataEdit.js';

export default class DrawingToolKit {
  constructor(dragCamera, imageDataManager, changeTracker, selection, colorManager, eyedropperPopup, imageResizer, setModewithUI, requestDraw, getArrowKeys) {
    //-- public
    this.updateSelectedMode = () => {};
    this.penThick = 1;
    this.penForce1px = false;
    this.eraserThick = 1;
    this.bucketMode = 'normal';
    this.bucketTarget = 'selectedLayer';
    this.bucketIncludeDiagonals = false;
    this.bucketSimilarColor = false;
    this.bucketDistRGB = 0;
    this.bucketDistA = 0;
    this.shapesThick = 1;
    this.shapesType = 'line';

    //-- private
    this._dragCamera = dragCamera;
    this._imageDataManager = imageDataManager;
    this._changeTracker = changeTracker;
    this._selection = selection;
    this._colorManager = colorManager;
    colorManager.register(color => this._setColor(color));
    this._eyedropperPopup = eyedropperPopup;
    this._imageResizer = imageResizer;
    this._setModewithUI = setModewithUI;
    this._requestDraw = requestDraw;
    this._getArrowKeys = getArrowKeys;
    this._mode = 'pen';
    this._orgMode = 'pen';
    this._pixelColor = [0, 0, 0, 255];
    this._sleep = false;
    this._lastClickTime = 0;
    this._lastClickX = -1;
    this._lastClickY = -1;
    this._seq = 0;
  }

  //--- モード変更と必要な初期化
  setMode(mode) {
    if(mode.indexOf('tmp') === 0 || mode === 'eyedropper') {
      this._orgMode = this._mode;
    }
    switch(mode) {
      case 'pen':
        this.penForce1px = false;
        break;
      case 'pixel-perfect-pen':
        this.penForce1px = true;
        break;
      case 'selection':
        this._selection.setMode('rect');
        break;
      case 'lasso':
        this._selection.setMode('lasso');
        break;
    }
    this._mode = mode;
  }
  getMode() {
    return this._mode;
  }
  revertMode() {
    if(this._mode.indexOf('tmp') === 0 || this._mode === 'eyedropper') {
      this._mode = this._orgMode;
    }
  }

  //--- imageDateはaが0~255で扱い辛いので、0~1で取得／設定
  _setColor([r, g, b, a]) {
    if(a === 0) r = g = b = 0;
    this._pixelColor = this._colorManager.convertTo8bit([r, g, b, a]);
  }

  //--- 取るべきCSSのcursorの値
  getCursor() {
    switch(this._mode) {
    case 'pen': case 'pixel-perfect-pen': return this.penForce1px ? 'var(--cursor-pen2)' : 'var(--cursor-pen)';
    case 'eraser': return 'var(--cursor-eraser)';
    case 'eyedropper': return 'var(--cursor-eyedropper)';
    case 'bucket':  return 'var(--cursor-bucket)';
    case 'shapes': return 'var(--cursor-shapes)';
    }
  }

  //--- ツールの太さ
  _getThick() {
    switch(this._mode) {
    case 'pen':
      return this.penThick;
    case 'eraser':
      return this.eraserThick;
    case 'shapes':
      if(this.shapesType === 'line') {
        return this.shapesThick;
      }
      break;
    }
    return 1;
  }
  getThickRect(centerX, centerY) {
    return this._imageDataManager.getThickRect(centerX, centerY, this._getThick());
  }

  //--- 使用状態か否か
  isFree() {
    return this._seq === 0;
  }

  //--- マウスイベント
  start(cursorX, cursorY, detail) {
    const [x, y] = this._dragCamera.localPtToWorldPt(cursorX, cursorY).map(Math.floor);
    switch(this._mode) {
    case 'pen':
    case 'pixel-perfect-pen':
      {
        if(detail >= 2 && this._lastClickX === x && this._lastClickY === y) {
          {
            const track = this._changeTracker.lastTrack;
            if(track?.name === 'confirm-pending' && track.lastClickTime === this._lastClickTime) {
              this._changeTracker.undo(true);
            }
          }
          if(this._imageDataManager.withinImage(x, y)) {
            const color = this._imageDataManager.getPixelColor(x, y);
            const arrowKeys = this._getArrowKeys();
            if(arrowKeys.any) {
              this._changeTracker.do('bucket-start', this._pixelColor, x, y, 'addEdge', false, 0, 0, arrowKeys);
            } else if(ImageDataEdit.isSameColor(color, 0, this._pixelColor, 0)) {
              this._changeTracker.do('bucket-start', this._pixelColor, x, y, 'eraseJaggies', false, 0, 0);
            } else {
              this._changeTracker.do('bucket-start', this._pixelColor, x, y, 'normal', false, 0, 0);
            }
            this.setMode('tmpBucket');
          } else {
            this._sleep = true;
          }
        } else {
          this._changeTracker.do('pen-start', this._pixelColor, x, y, this.penThick, this.penForce1px);
        }
        this._lastClickTime = performance.now();
        this._lastClickX = x;
        this._lastClickY = y;
        this._requestDraw();
      }
      break;
    case 'eraser':
      this._changeTracker.do('pen-start', [0,0,0,0], x, y, this.eraserThick, false);
      this._requestDraw();
      break;
    case 'eyedropper':
      this._pickingColor = this._imageDataManager.getPixelColor(x, y);
      this._eyedropperPopup.popup(this._colorManager.normalize(this._pickingColor), cursorX, cursorY);
      break;
    case 'bucket':
      if(!this._imageDataManager.withinImage(x, y)) {
        this._sleep = true;
        return;
      }
      /*if(this._isBucketAll()) {
        const prevTrack = this._changeTracker.lastTrack;
        this._changeTracker.do(
          'bucket-all', this._pixelColor, x, y, this._bucketDistRGB(), this._bucketDistA(), this.bucketTarget
        );
        const currentTrack = this._changeTracker.lastTrack;
        if(prevTrack !== currentTrack) {
          this._lastTrack = currentTrack;
        } else {
          this._lastTrack = null;
        }
      } else {*/
        this._changeTracker.do(
          'bucket-start', this._pixelColor, x, y, this.bucketMode, this.bucketIncludeDiagonals,
          this._bucketDistRGB(), this._bucketDistA(), this._getArrowKeys()
        );
      //}
      this._requestDraw();
      break;
    case 'shapes':
      if(this.shapesType !== 'curve' || this._seq === 0) {
        this._changeTracker.do('shapes-start', this._pixelColor, x, y, this.shapesThick, this.shapesType);
        this._startX = this._lastX = x;
        this._startY = this._lastY = y;
        this._requestDraw();
      }
      break;
    }
  }
  cancel() {
    if(this._sleep) {
      this._sleep = false;
      return;
    }
    switch(this._mode) {
    case 'pen':
    case 'pixel-perfect-pen':
    case 'eraser':
    case 'tmpBucket':
    case 'shapes':
      this._changeTracker.do('cancel-pending');
      this._seq = 0;
      this._imageResizer.wake();
      this._requestDraw();
      break;
    case 'bucket':
      /*if(this._isBucketAll()) {
        if(this._lastTrack === this._changeTracker.lastTrack) {
          this._changeTracker.undo();
        }
      } else {*/
        this._changeTracker.do('cancel-pending');
      //}
      this._requestDraw();
      break;
    case 'eyedropper':
      this._setModewithUI(this._orgMode);
      this._eyedropperPopup.hide();
      break;
    }
    this._returnTmpMode();
  }
  drag(cursorX, cursorY) {
    if(this._sleep) return;
    const [x, y] = this._dragCamera.localPtToWorldPt(cursorX, cursorY).map(Math.floor);
    switch(this._mode) {
    case 'pen':
    case 'pixel-perfect-pen':
      this._changeTracker.do('pen-drag', x, y, this.penThick, this.penForce1px);
      this._requestDraw();
      break;
    case 'eraser':
      this._changeTracker.do('pen-drag', x, y, this.eraserThick, false);
      this._requestDraw();
      break;
    case 'eyedropper':
      {
        const color = this._changeTracker._imageDataManager.getPixelColor(x, y);
        if(this._pickingColor.some((v, i) => v !== color[i])) {
          this._pickingColor = color;
          this._eyedropperPopup.popup(this._colorManager.normalize(this._pickingColor), cursorX, cursorY);
        } else {
          this._eyedropperPopup.move(cursorX, cursorY);
        }
      }
      break;
    case 'shapes':
      if(this.shapesType !== 'curve' || this._seq === 0) {
        this._changeTracker.do('shapes-drag', x, y);
        this._lastX = x;
        this._lastY = y;
        this._requestDraw();
      }
      break;
    }
  }
  nodrag(cursorX, cursorY) {
    if(this._sleep) return;
    const [x, y] = this._dragCamera.localPtToWorldPt(cursorX, cursorY).map(Math.floor);
    switch(this._mode) {
    case 'shapes':
      if(this.shapesType === 'curve' && this._seq === 1) {
        this._changeTracker.do('shapes-nodrag-curve', x, y, this._lastX, this._lastY);
        this._requestDraw();
      }
      break;
    }
  }
  finish() {
    if(this._sleep) {
      this._sleep = false;
      return;
    }
    switch(this._mode) {
    case 'pen':
    case 'pixel-perfect-pen':
      this._changeTracker.do('confirm-pending', this._lastClickTime);
      break;
    case 'eraser':
    case 'tmpBucket':
      this._changeTracker.do('confirm-pending', null);
      break;
    case 'shapes':
      if(this.shapesType === 'curve' && this._seq === 0 && (this._startX !== this._lastX || this._startY !== this._lastY)) {
        this._seq = 1;
        this._imageResizer.sleep();
      } else {
        this._changeTracker.do('confirm-pending', null);
        this._seq = 0;
        this._imageResizer.wake();
      }
      this._requestDraw();
      break;
    case 'bucket':
      if(this.bucketMode === 'replaceColor') {
        if(this.bucketTarget === 'allFrames') {
          this._changeTracker.do('replace-color-all', this._bucketDistRGB(), this._bucketDistA());
        } else if(this.bucketTarget === 'selectedFrame') {
          this._changeTracker.do('replace-color', this._bucketDistRGB(), this._bucketDistA());
        } else {
          this._changeTracker.do('confirm-pending', null);
        }
      } else {
        this._changeTracker.do('confirm-pending', null);
      }
      break;
    case 'eyedropper':
      this._colorManager.set(this._colorManager.normalize(this._pickingColor));
      this._setModewithUI(this._orgMode);
      this._eyedropperPopup.hide();
      break;
    }
    this._returnTmpMode();
  }
  _returnTmpMode() {
    switch(this._mode) {
    case 'tmpBucket':
      this.setMode(this._orgMode);
      break;
    }
  }
  _isBucketAll() {
    return this.bucketMode === 'replaceColor' && this.bucketTarget !== 'selectedLayer';
  }
  _bucketDistRGB() {
    return this.bucketSimilarColor ? this.bucketDistRGB : 0;
  }
  _bucketDistA() {
    return this.bucketSimilarColor ? this.bucketDistA : 0;
  }
  finishIfNot() {
    switch(this._mode) {
    case 'shapes':
      if(this.shapesType === 'curve' && this._seq !== 0) {
        this.finish();
      }
      break;
    }
  }
  cancelIfNotFinished() {
    switch(this._mode) {
    case 'shapes':
      if(this.shapesType === 'curve' && this._seq !== 0) {
        this.cancel();
      }
      break;
    }
  }
}
